import { memo, useMemo } from 'react'
import { ArrowRightIcon } from '@panwds/icons'
import { Button, LoadingIcon, LoadingPanel, Sheet, SheetBody, Tooltip, classNames } from '@panwds/react-ui'
import TSGTree, { type RenderActionsFn } from '../hub/components/common/TSGTree'
import AppsCard from './AppsCard'
import AppsList from './AppsList'
import { launchMultiTenantView, launchSingleTenantView } from '../common/utils/Sparky'
import { inAscPath, inMspPath, renderTsgPath } from './store/computed'
import './SwitcherSheet.scss'
import type { MutableRefObject, VFC } from 'react'
import type { ReadonlyTsgTree, Tsg } from '@sparky/framework'
import type { TsgAppInstances, StateRef } from './store'

type SwitcherSheetProps = {
  isOpen: boolean
  isHub: boolean
  stateRef: StateRef
  isSingleTenantOnly: boolean
  close: () => void
  selectTsg: (tsg: Tsg | string | undefined) => void
  leftRef: MutableRefObject<HTMLElement>
  tsgAppInstances: TsgAppInstances | undefined
  theme: string | undefined
  contextTsg: Tsg | undefined
  tsg: Tsg | undefined
  tsgs: ReadonlyTsgTree
}

const SwitcherSheet: VFC<SwitcherSheetProps> = ({ isOpen, stateRef, close, theme, tsgAppInstances, leftRef, contextTsg, isHub, isSingleTenantOnly, selectTsg, tsg, tsgs }) => {
  const selectedTsgId = tsg?.id

  const renderActions = useMemo(() => {
    const switchTsgInCurrentApp = () => {
      if (selectedTsgId) {
        const { pathname, search } = window.location
        const launch = inMspPath() || inAscPath() ? launchMultiTenantView : launchSingleTenantView
        launch(selectedTsgId, `${pathname}${search}`)
      }
    }
    const render: RenderActionsFn = ({ tsg }) => {
      if (isHub || contextTsg?.id === tsg.id || tsg.id !== selectedTsgId ||
        (tsgAppInstances && tsgAppInstances?.tsg.id !== selectedTsgId)) {
        return null
      }
      if (!tsgAppInstances) {
        return <LoadingIcon size='md' className='cm-m-1 cm-opacity-50 tw-animate-spin' />
      }
      const canSwitch = stateRef.current.currentPortalDisplayAppId && tsgAppInstances.some(inst => {
        return !inst.isCurrent && inst.app.app_id === stateRef.current.currentPortalDisplayAppId && inst.canLaunch
      })
      if (!canSwitch) {
        return null
      }
      return <Tooltip label='Switch to this tenant' defaultPlacement='right'>
        <Button
          addClassName='switcher-action-launch'
          icon={<ArrowRightIcon size='xs' className='cm-text-primary' />}
          appearance='tertiary-clear'
          aria-label='Switch Tenant'
          size='sm'
          onClick={switchTsgInCurrentApp}
          dataTestId='switcher-action-launch-btn'
        />
      </Tooltip>
    } // as RenderActionsFn
    return render
  }, [selectedTsgId, isHub, contextTsg?.id, tsgAppInstances, stateRef])

  const singleColumn = isSingleTenantOnly || isHub

  const tsgTree = isSingleTenantOnly ? null : (
    <div className={classNames('switcher-sheet-tsg-tree cm-h-full cm-flex cm-flex-col', singleColumn && 'cm-w-full')}>
      <TSGTree
        addSearchClassName='switcher-sheet-tsg-search cm-h-16 cm-border-0 cm-border-b cm-border-solid cm-border-gray-950 dark:cm-border-gray-950 tw-scrollbar-dark dark:tw-scrollbar-track-dark dark:tw-scrollbar-thumb-dark dark:tw-scrollbar-corner-dark'
        addTreeClassName='cm-pr-0'
        renderActions={isHub ? undefined : renderActions}
        {...{ tsg, tsgs, onSelect: selectTsg, selectedTsgId, isVisible: isOpen, showTsgIdInTree: false }}
      />
    </div>
  )

  const tsgApps = (() => { // no memo
    if (!selectedTsgId || isHub) {
      return null
    }
    const list = tsgAppInstances ? <AppsList
      tsgAppInstances={tsgAppInstances}
      onClose={close}
    /> : <LoadingPanel />
    return (
      <div className='switcher-sheet-tsg-apps cm-overflow-hidden cm-h-full cm-flex cm-flex-col cm-flex-1'>
        <AppsCard tsg={tsgAppInstances?.tsg || tsg} tsgs={tsgs} isSingleTenantOnly={isSingleTenantOnly}>{list}</AppsCard>
      </div>
    )
  })()

  const cls = classNames(
    'switcher-sheet cm-bg-blue-steel-950 tw-dark',
    theme === 'dark' ? 'sparky-theme-dark' : 'sparky-theme-light',
    isOpen && !singleColumn ? 'switcher-sheet-open !cm-w-[40rem]' : 'cm-max-w-[20rem]',
  )
  const subtitle = useMemo(() => {
    if (!contextTsg) {
      return
    }
    const tsgIdDisplay = <span className='cm-block cm-text-gray-100 dark:cm-text-gray-100'>TSG ID: {contextTsg.id}</span>
    const paths = renderTsgPath(contextTsg, tsgs)
    if (paths) {
      return (
        <span className='cm-block'>
          {tsgIdDisplay}
          <span className='cm-block cm-text-gray-400 dark:cm-text-gray-400'>{paths}</span>
        </span>
      )
    }
    return tsgIdDisplay
  }, [contextTsg, tsgs]) as string | undefined
  return (
    <Sheet
      title={contextTsg?.display_name || 'Tenant'}
      subtitle={subtitle}
      position='permanent'
      leftRef={leftRef as MutableRefObject<HTMLDivElement>}
      showMask={true}
      isOpen={isOpen}
      onClose={close}
      addClassName={cls}
      dataTestId='sparky-switcher-sheet'
    >
      <SheetBody addClassName='cm-flex cm-flex-row cm-overflow-hidden cm-p-0 cm-overflow-hidden'>
        {tsgTree}
        {tsgApps}
      </SheetBody>
    </Sheet>
  )
}

export default memo(SwitcherSheet)
